/**
 * System Characteristic Management Page
 * Admin interface for managing global system-level prompts/instructions
 *
 * Important: System characteristics are global, admin-owned, and high-impact
 * Changes affect all agents using them
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Plus, Pencil, Eye, RefreshCcw, AlertTriangle, Lock, Globe2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useSystemCharacteristics,
  useCreateSystemCharacteristic,
  useUpdateSystemCharacteristic,
} from '../../services/systemCharacteristic.service';
import { SystemCharacteristic, CreateSystemCharacteristicInput } from '../../types/admin';
import { EmptyState } from '../../components/admin/EmptyState';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';

const CHARACTERISTIC_LAYERS = [
  { value: 'identity', label: 'Identity' },
  { value: 'tone_style', label: 'Tone & Style' },
  { value: 'values', label: 'Values & Priorities' },
  { value: 'behavior', label: 'Behavioral Rules' },
  { value: 'constraints', label: 'Constraints' },
  { value: 'domain', label: 'Domain Guidance' },
];

const CHARACTERISTIC_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const CHARACTERISTIC_VISIBILITIES = [
  {
    value: 'private',
    label: 'Private',
    helper: 'Only available to internal system/admin workflows',
  },
  {
    value: 'public',
    label: 'Public',
    helper: 'Available for users to attach to their agents',
  },
];

const LAYER_LABELS = CHARACTERISTIC_LAYERS.reduce<Record<string, string>>((acc, layer) => {
  acc[layer.value] = layer.label;
  return acc;
}, {});

const getCharacteristicOwnerLabel = (characteristic: SystemCharacteristic) => {
  if (characteristic.isSystem) return 'System';

  const owner = characteristic.ownerUser;
  if (!owner) return 'Unknown user';

  return owner.fullName || owner.userName || owner.email;
};

// ============================================
// Create/Edit Form Dialog Component
// ============================================

interface SystemCharacteristicFormDialogProps {
  open: boolean;
  onClose: () => void;
  characteristic?: SystemCharacteristic | null;
}

const SystemCharacteristicFormDialog: React.FC<SystemCharacteristicFormDialogProps> = ({
  open,
  onClose,
  characteristic,
}) => {
  const isEditMode = !!characteristic;
  const createMutation = useCreateSystemCharacteristic();
  const updateMutation = useUpdateSystemCharacteristic();

  const [formValues, setFormValues] = useState({
    code: characteristic?.code || '',
    name: characteristic?.name || '',
    description: characteristic?.description || '',
    layer: characteristic?.layer || 'behavior',
    prompt: characteristic?.prompt || '',
    sortOrder: characteristic?.sortOrder ?? 100,
    priority: characteristic?.priority ?? 100,
    status: characteristic?.status || 'published',
    visibility: characteristic?.visibility === 'private' ? 'private' : 'public',
    isDefault: characteristic?.isDefault ?? false,
  });

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Update form when characteristic changes
  React.useEffect(() => {
    if (characteristic) {
      setFormValues({
        code: characteristic.code,
        name: characteristic.name,
        description: characteristic.description || '',
        layer: characteristic.layer || 'behavior',
        prompt: characteristic.prompt,
        sortOrder: characteristic.sortOrder ?? 100,
        priority: characteristic.priority ?? 100,
        status: characteristic.status || 'published',
        visibility: characteristic.visibility === 'private' ? 'private' : 'public',
        isDefault: characteristic.isDefault ?? false,
      });
    } else {
      setFormValues({
        code: '',
        name: '',
        description: '',
        layer: 'behavior',
        prompt: '',
        sortOrder: 100,
        priority: 100,
        status: 'published',
        visibility: 'private',
        isDefault: false,
      });
    }
    setErrors({});
  }, [characteristic, open]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formValues.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[a-z0-9_-]+$/.test(formValues.code)) {
      newErrors.code =
        'Code must contain only lowercase letters, numbers, hyphens, and underscores';
    }

    if (!formValues.prompt.trim()) {
      newErrors.prompt = 'Prompt is required';
    }

    if (!formValues.layer) {
      newErrors.layer = 'Layer is required';
    }

    if (!formValues.status) {
      newErrors.status = 'Status is required';
    }

    if (!formValues.visibility) {
      newErrors.visibility = 'Visibility is required';
    }

    if (formValues.sortOrder < 0 || formValues.sortOrder > 10000) {
      newErrors.sortOrder = 'Sort order must be between 0 and 10000';
    }

    if (formValues.priority < 0 || formValues.priority > 10000) {
      newErrors.priority = 'Priority must be between 0 and 10000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const data: CreateSystemCharacteristicInput = {
      code: formValues.code.trim(),
      name: formValues.name.trim(),
      description: formValues.description.trim() || null,
      layer: formValues.layer,
      prompt: formValues.prompt.trim(),
      sortOrder: formValues.sortOrder,
      priority: formValues.priority,
      status: formValues.status,
      visibility: formValues.visibility as 'public' | 'private',
      isDefault: formValues.isDefault,
    };

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({
          publicId: characteristic.publicId,
          name: formValues.name.trim(),
          description: formValues.description.trim() || null,
          layer: formValues.layer,
          prompt: formValues.prompt.trim(),
          sortOrder: formValues.sortOrder,
          priority: formValues.priority,
          status: formValues.status,
          visibility: formValues.visibility as 'public' | 'private',
          isDefault: formValues.isDefault,
          // Note: code is immutable and cannot be updated
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (error) {
      // Error handled by mutation hooks
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange =
    (field: 'layer' | 'status' | 'visibility') => (event: SelectChangeEvent<string>) => {
      handleChange(field, event.target.value);
    };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="bg-slate-50 dark:bg-slate-800">
        <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
          {isEditMode ? 'Edit System Characteristic' : 'Create System Characteristic'}
        </Typography>
      </DialogTitle>

      <DialogContent className="mt-4">
        {/* Warning Banner */}
        <Alert severity="warning" className="mb-4" icon={<AlertTriangle size={20} />}>
          <Typography variant="body2" className="font-medium">
            Private characteristics are for internal system use. Public characteristics can be
            attached by users to their agents.
          </Typography>
        </Alert>

        <Box className="space-y-4">
          {/* Name Field */}
          <TextField
            fullWidth
            label="Name"
            value={formValues.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Display name for the system characteristic'}
            disabled={isLoading}
            required
          />

          {/* Code Field */}
          <TextField
            fullWidth
            label="Code"
            value={formValues.code}
            onChange={(e) => handleChange('code', e.target.value)}
            error={!!errors.code}
            helperText={
              errors.code ||
              (isEditMode
                ? 'Code is immutable and cannot be changed'
                : 'Unique identifier (lowercase, hyphens, underscores)')
            }
            disabled={isLoading || isEditMode} // Code is immutable after creation
            required
            InputProps={{
              style: { fontFamily: 'monospace' },
            }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formValues.description}
            onChange={(e) => handleChange('description', e.target.value)}
            helperText="Short admin note for what this characteristic controls"
            disabled={isLoading}
          />

          <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormControl fullWidth error={!!errors.layer} disabled={isLoading} required>
              <InputLabel>Layer</InputLabel>
              <Select label="Layer" value={formValues.layer} onChange={handleSelectChange('layer')}>
                {CHARACTERISTIC_LAYERS.map((layer) => (
                  <MenuItem key={layer.value} value={layer.value}>
                    {layer.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.layer || 'Prompt section used during compilation'}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth error={!!errors.status} disabled={isLoading} required>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={formValues.status}
                onChange={handleSelectChange('status')}
              >
                {CHARACTERISTIC_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.status || 'Only published characteristics can be attached to agents'}
              </FormHelperText>
            </FormControl>
          </Box>

          <FormControl fullWidth error={!!errors.visibility} disabled={isLoading} required>
            <InputLabel>Visibility</InputLabel>
            <Select
              label="Visibility"
              value={formValues.visibility}
              onChange={handleSelectChange('visibility')}
            >
              {CHARACTERISTIC_VISIBILITIES.map((visibility) => (
                <MenuItem key={visibility.value} value={visibility.value}>
                  {visibility.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.visibility ||
                CHARACTERISTIC_VISIBILITIES.find((item) => item.value === formValues.visibility)
                  ?.helper}
            </FormHelperText>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formValues.isDefault}
                onChange={(event) => handleChange('isDefault', event.target.checked)}
                disabled={isLoading}
              />
            }
            label="Default fallback for this layer"
          />
          <Typography variant="caption" className="-mt-3 block text-gray-500 dark:text-slate-500">
            Used automatically when an agent has no configured characteristics.
          </Typography>

          <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormattedNumberTextField
              fullWidth
              label="Sort Order"
              value={formValues.sortOrder}
              onValueChange={(value) => handleChange('sortOrder', value ?? 0)}
              error={!!errors.sortOrder}
              helperText={errors.sortOrder || 'Lower values appear earlier within the same layer'}
              disabled={isLoading}
              inputProps={{ min: 0, max: 10000 }}
            />

            <FormattedNumberTextField
              fullWidth
              label="Priority"
              value={formValues.priority}
              onValueChange={(value) => handleChange('priority', value ?? 0)}
              error={!!errors.priority}
              helperText={errors.priority || 'Reserved for conflict resolution and future ranking'}
              disabled={isLoading}
              inputProps={{ min: 0, max: 10000 }}
            />
          </Box>

          {/* Prompt Field */}
          <TextField
            fullWidth
            label="Prompt"
            value={formValues.prompt}
            onChange={(e) => handleChange('prompt', e.target.value)}
            error={!!errors.prompt}
            helperText={errors.prompt || 'The system-level instruction/prompt'}
            disabled={isLoading}
            required
            multiline
            rows={8}
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions className="px-6 pb-4">
        <Button
          onClick={onClose}
          disabled={isLoading}
          className="text-gray-600 dark:text-slate-400"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          className="bg-blue-600 text-white hover:bg-blue-700"
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// System Characteristic Management Component
// ============================================

export const SystemCharacteristicList: React.FC = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'system' | 'user'>('system');
  const [selectedCharacteristic, setSelectedCharacteristic] = useState<SystemCharacteristic | null>(
    null
  );

  const { data: characteristics, isLoading, error } = useSystemCharacteristics();
  const allCharacteristics = characteristics || [];
  const systemCharacteristics = React.useMemo(
    () => allCharacteristics.filter((item) => item.isSystem),
    [allCharacteristics]
  );
  const userCharacteristics = React.useMemo(
    () => allCharacteristics.filter((item) => !item.isSystem),
    [allCharacteristics]
  );
  const displayedCharacteristics =
    activeTab === 'system' ? systemCharacteristics : userCharacteristics;

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 230,
      renderCell: (params) => (
        <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" className="font-mono text-sm text-gray-700 dark:text-slate-300">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'ownerUser',
      headerName: 'Owner',
      width: 220,
      valueGetter: (_value, row) => getCharacteristicOwnerLabel(row as SystemCharacteristic),
      renderCell: (params) => {
        const characteristic = params.row as SystemCharacteristic;
        return (
          <Box>
            <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
              {getCharacteristicOwnerLabel(characteristic)}
            </Typography>
            {!characteristic.isSystem && characteristic.ownerUser?.email && (
              <Typography variant="caption" className="text-gray-500 dark:text-slate-500">
                {characteristic.ownerUser.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'layer',
      headerName: 'Layer',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={LAYER_LABELS[params.value] || params.value || 'Behavior'}
          size="small"
          className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = params.value || 'published';
        return (
          <Chip
            label={status}
            size="small"
            className={
              status === 'published'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : status === 'draft'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
            }
          />
        );
      },
    },
    {
      field: 'visibility',
      headerName: 'Visibility',
      width: 130,
      renderCell: (params) => {
        const visibility = params.value === 'private' ? 'private' : 'public';
        return (
          <Chip
            icon={visibility === 'public' ? <Globe2 size={14} /> : <Lock size={14} />}
            label={visibility === 'public' ? 'Public' : 'Private'}
            size="small"
            className={
              visibility === 'public'
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
            }
          />
        );
      },
    },
    {
      field: 'isDefault',
      headerName: 'Default',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Default' : 'No'}
          size="small"
          className={
            params.value
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
          }
        />
      ),
    },
    {
      field: 'source',
      headerName: 'Storage',
      width: 130,
      renderCell: (params) => {
        const source = params.value || 'database';
        return (
          <Chip
            label={source === 'default' ? 'Default' : 'Database'}
            size="small"
            className={
              source === 'default'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
            }
          />
        );
      },
    },
    {
      field: 'usageCount',
      headerName: 'Usage Count',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          className={
            params.value > 0
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
          }
        />
      ),
    },
    {
      field: 'prompt',
      headerName: 'Prompt Preview',
      width: 280,
      renderCell: (params) => (
        <Typography
          variant="body2"
          className="truncate font-mono text-xs text-gray-600 dark:text-slate-400"
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isEditable = params.row.isEditable !== false;

        return (
          <Box className="flex gap-1">
            <IconButton
              size="small"
              onClick={() => navigate(`/system-characteristics/${params.row.publicId}`)}
              className="text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-900/20"
              title="View Details"
            >
              <Eye size={16} />
            </IconButton>
            <Tooltip
              title={
                isEditable
                  ? 'Edit'
                  : 'User-owned characteristics are read-only in this admin screen'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedCharacteristic(params.row as SystemCharacteristic);
                    setDialogOpen(true);
                  }}
                  className="text-blue-600 hover:bg-blue-50 disabled:text-gray-400 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:disabled:text-slate-600"
                  disabled={!isEditable}
                >
                  <Pencil size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleCreate = () => {
    setSelectedCharacteristic(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCharacteristic(null);
  };

  const emptyState =
    activeTab === 'system'
      ? {
          title: 'No System Characteristics',
          description: 'Create your first system characteristic to get started',
          actionLabel: 'Add System Characteristic',
          onAction: handleCreate,
        }
      : {
          title: 'No User Characteristics',
          description: 'User-created characteristics will appear here.',
          actionLabel: undefined,
          onAction: undefined,
        };

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          Characteristics
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Review system and user-owned prompts. Only system characteristics are editable here.
        </Typography>
      </Box>

      {/* Table Card */}
      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        {/* Toolbar */}
        <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Characteristics
          </Typography>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleCreate}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Add System Characteristic
          </Button>
        </Box>

        <Box className="border-b border-gray-200 px-4 dark:border-slate-700">
          <Tabs
            value={activeTab}
            onChange={(_, value: 'system' | 'user') => setActiveTab(value)}
            aria-label="Characteristic owner tabs"
          >
            <Tab value="system" label={`System (${systemCharacteristics.length})`} />
            <Tab value="user" label={`User (${userCharacteristics.length})`} />
          </Tabs>
        </Box>

        {/* DataGrid */}
        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12">
            <Typography className="text-center text-red-600 dark:text-red-400">
              Failed to load characteristics. Please try again.
            </Typography>
          </Box>
        ) : displayedCharacteristics.length > 0 ? (
          <DataGrid
            rows={displayedCharacteristics}
            columns={columns}
            getRowId={(row) => row.publicId}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12">
            <EmptyState
              Icon={RefreshCcw}
              title={emptyState.title}
              description={emptyState.description}
              actionLabel={emptyState.actionLabel}
              onAction={emptyState.onAction}
            />
          </Box>
        )}
      </Box>

      {/* Form Dialog */}
      <SystemCharacteristicFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        characteristic={selectedCharacteristic}
      />
    </Box>
  );
};
